import { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { FileUpload } from 'primereact/fileupload';
import type { FileUpload as FileUploadType } from 'primereact/fileupload';
import { InputText } from 'primereact/inputtext';
import {
  adminDeleteTemplate,
  adminDownloadTemplate,
  adminGetTemplateBytes,
  adminGetTemplateRules,
  adminListTemplates,
  adminListUsers,
  adminLockUser,
  adminSaveTemplateRules,
  adminUnlockUser,
  adminUnwatchUser,
  adminUploadTemplates,
  adminWatchUser,
} from '../api';
import type { AdminUsersResponse, TemplateRule } from '../api';
import type { StatusRecord } from '../ws';

function fileExtension(name: string): string {
  return name.split('.').pop()?.toLowerCase() ?? '';
}

function FileTypeBadge({ name }: { name: string }) {
  const ext = fileExtension(name);
  if (ext === 'docx') return <span className="file-type-badge file-type-badge--docx">docx</span>;
  if (ext === 'xlsx') return <span className="file-type-badge file-type-badge--xlsx">xlsx</span>;
  return <span className="file-type-badge" style={{ background: '#f1f5f9', color: '#64748b' }}>{ext}</span>;
}

type PreviewData = { filename: string; html: string };

export function AdminPage({ activityFeed }: { activityFeed: StatusRecord[] }) {
  const uploadRef = useRef<FileUploadType | null>(null);

  const [users, setUsers] = useState<AdminUsersResponse>({ connected: [], known: [] });
  const [selectedEmail, setSelectedEmail] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [templates, setTemplates] = useState<string[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [editing, setEditing] = useState(false);
  const [watching, setWatching] = useState(false);
  const [rules, setRules] = useState<Record<string, TemplateRule>>({});
  const [rulesDirty, setRulesDirty] = useState(false);
  const [rulesSaving, setRulesSaving] = useState(false);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);

  const emptyRule = (): TemplateRule => ({ folder: '', filenameTemplate: '' });

  const updateRule = (filename: string, field: keyof TemplateRule, value: string) => {
    setRules(prev => ({ ...prev, [filename]: { ...(prev[filename] ?? emptyRule()), [field]: value } }));
    setRulesDirty(true);
  };

  const handleSaveRules = async () => {
    if (!selectedEmail) return;
    setRulesSaving(true);
    try {
      const active = Object.fromEntries(Object.entries(rules).filter(([name]) => templates.includes(name)));
      await adminSaveTemplateRules(selectedEmail, active);
      setRules(active);
      setRulesDirty(false);
    } finally {
      setRulesSaving(false);
    }
  };

  const refreshUsers = async () => {
    setLoadingUsers(true);
    try {
      setUsers(await adminListUsers());
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    void refreshUsers();
  }, []);

  const selectUser = async (email: string) => {
    // Stop ongoing session for previous user without notifying (best-effort)
    if (selectedEmail && editing) adminUnlockUser(selectedEmail).catch(() => {});
    if (selectedEmail && watching) adminUnwatchUser(selectedEmail).catch(() => {});
    setEditing(false);
    setWatching(false);
    setSelectedEmail(email);
    setTemplates([]);
    setRules({});
    setRulesDirty(false);
    setLoadingTemplates(true);
    try {
      const [tmpl, rulesData] = await Promise.all([
        adminListTemplates(email),
        adminGetTemplateRules(email),
      ]);
      setTemplates(tmpl);
      setRules(rulesData);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleStartEditing = async () => {
    if (!selectedEmail) return;
    await adminLockUser(selectedEmail);
    setEditing(true);
  };

  const handleStopEditing = async () => {
    if (!selectedEmail) return;
    await adminUnlockUser(selectedEmail);
    setEditing(false);
  };

  const handleToggleWatch = async () => {
    if (!selectedEmail) return;
    if (watching) {
      await adminUnwatchUser(selectedEmail);
      setWatching(false);
    } else {
      await adminWatchUser(selectedEmail);
      setWatching(true);
    }
  };

  const handleManualSelect = () => {
    const email = manualEmail.trim();
    if (email) selectUser(email);
  };

  const handleUpload = async (event: { files: File[] }) => {
    if (!selectedEmail) return;
    await adminUploadTemplates(selectedEmail, event.files);
    uploadRef.current?.clear();
    setTemplates(await adminListTemplates(selectedEmail));
  };

  const handleDelete = async (filename: string) => {
    if (!selectedEmail) return;
    await adminDeleteTemplate(selectedEmail, filename);
    setTemplates(prev => prev.filter(t => t !== filename));
  };

  const openPreview = async (filename: string) => {
    if (!selectedEmail) return;
    setPreviewLoading(filename);
    try {
      const buffer = await adminGetTemplateBytes(selectedEmail, filename);
      let html = '';
      if (filename.toLowerCase().endsWith('.docx')) {
        const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
        html = result.value;
      } else {
        const workbook = XLSX.read(buffer, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        html = XLSX.utils.sheet_to_html(sheet);
      }
      setPreview({ filename, html: DOMPurify.sanitize(html) });
    } finally {
      setPreviewLoading(null);
    }
  };

  return (
    <>
      {/* ── Preview dialog ── */}
      <Dialog
        visible={preview !== null}
        onHide={() => setPreview(null)}
        header={
          preview && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <FileTypeBadge name={preview.filename} />
              <span>{preview.filename}</span>
            </div>
          )
        }
        style={{ width: '70vw', maxWidth: '1000px' }}
        modal
        dismissableMask
      >
        {preview && (
          <iframe
            srcDoc={preview.html}
            style={{ width: '100%', height: '60vh', border: 'none' }}
            sandbox="allow-same-origin"
            title={`Preview: ${preview.filename}`}
          />
        )}
      </Dialog>

      {/* ── Connected users ── */}
      <Card className="section-card">
        <div className="section-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <h3 className="m-0">Connected Users</h3>
            {users.connected.length > 0 && (
              <span className="count-badge">{users.connected.length}</span>
            )}
          </div>
          <Button
            icon={loadingUsers ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'}
            text
            rounded
            size="small"
            onClick={refreshUsers}
            tooltip="Refresh"
            tooltipOptions={{ position: 'left' }}
            disabled={loadingUsers}
          />
        </div>

        {users.connected.length === 0 ? (
          <div className="template-empty">
            <i className="pi pi-wifi" />
            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>No users connected</div>
            <div style={{ fontSize: '0.82rem' }}>Users appear here when they have an active WebSocket session.</div>
          </div>
        ) : (
          <div>
            {users.connected.map(email => (
              <div key={email} className="template-row">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: '0.5rem',
                      height: '0.5rem',
                      borderRadius: '50%',
                      background: '#22c55e',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: '0.88rem' }}>{email}</span>
                </div>
                <Button
                  label="Manage templates"
                  icon="pi pi-folder-open"
                  size="small"
                  text
                  onClick={() => selectUser(email)}
                />
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <InputText
            value={manualEmail}
            onChange={e => setManualEmail(e.target.value)}
            placeholder="Or enter user email manually…"
            style={{ flex: 1, fontSize: '0.85rem' }}
            onKeyDown={e => e.key === 'Enter' && handleManualSelect()}
          />
          <Button
            label="Manage"
            icon="pi pi-folder-open"
            size="small"
            onClick={handleManualSelect}
            disabled={!manualEmail.trim()}
          />
        </div>
      </Card>

      {/* ── Session controls ── */}
      {selectedEmail && (
        <Card className="section-card">
          <div className="section-header">
            <div>
              <h3 className="m-0">Session</h3>
              <p className="m-0 mt-1" style={{ fontSize: '0.82rem', color: '#64748b' }}>
                Active user: <strong>{selectedEmail}</strong>
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {editing ? (
                <Button
                  label="Stop editing"
                  icon="pi pi-lock-open"
                  severity="warning"
                  size="small"
                  onClick={handleStopEditing}
                  tooltip="Unlock user's interface"
                  tooltipOptions={{ position: 'left' }}
                />
              ) : (
                <Button
                  label="Start editing"
                  icon="pi pi-lock"
                  size="small"
                  onClick={handleStartEditing}
                  tooltip="Lock user's interface and enter edit mode"
                  tooltipOptions={{ position: 'left' }}
                />
              )}
              <Button
                label={watching ? 'Stop watching' : 'Watch live feed'}
                icon={watching ? 'pi pi-eye-slash' : 'pi pi-eye'}
                severity={watching ? 'secondary' : undefined}
                size="small"
                outlined={!watching}
                onClick={handleToggleWatch}
              />
            </div>
          </div>

          {editing && (
            <div style={{
              background: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '0.4rem',
              padding: '0.5rem 0.75rem',
              fontSize: '0.82rem',
              color: '#92400e',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}>
              <i className="pi pi-lock" />
              User's interface is locked — they can see your changes in real time.
            </div>
          )}
        </Card>
      )}

      {/* ── Live feed ── */}
      {selectedEmail && watching && (
        <Card className="section-card">
          <div className="section-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <h3 className="m-0">Live Feed</h3>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.3rem',
                fontSize: '0.75rem',
                color: '#16a34a',
                fontWeight: 600,
              }}>
                <span style={{ width: '0.45rem', height: '0.45rem', borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                Live
              </span>
            </div>
          </div>
          <div style={{
            maxHeight: '14rem',
            overflowY: 'auto',
            fontFamily: 'ui-monospace, monospace',
            fontSize: '0.78rem',
            background: '#0f172a',
            color: '#e2e8f0',
            borderRadius: '0.4rem',
            padding: '0.75rem',
          }}>
            {activityFeed.filter(r => r.payload === selectedEmail).length === 0 ? (
              <span style={{ color: '#64748b' }}>Waiting for activity from {selectedEmail}…</span>
            ) : (
              activityFeed
                .filter(r => r.payload === selectedEmail)
                .map((r, i) => (
                  <div key={i} style={{ marginBottom: '0.2rem' }}>
                    <span style={{ color: '#64748b', marginRight: '0.5rem' }}>
                      {r.timestamp.toLocaleTimeString()}
                    </span>
                    {r.message}
                  </div>
                ))
            )}
          </div>
        </Card>
      )}

      {/* ── User templates ── */}
      {selectedEmail && (
        <Card className="section-card">
          <div className="section-header">
            <div>
              <h3 className="m-0">Templates</h3>
              <p className="m-0 mt-1" style={{ fontSize: '0.82rem', color: '#64748b' }}>
                Managing templates for <strong>{selectedEmail}</strong>
              </p>
            </div>
            <FileUpload
              ref={uploadRef}
              mode="basic"
              name="files"
              chooseLabel="Upload"
              chooseOptions={{ icon: 'pi pi-upload' }}
              multiple
              auto
              customUpload
              uploadHandler={handleUpload}
              accept=".docx,.xlsx"
            />
          </div>

          {loadingTemplates ? (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#94a3b8' }}>
              <i className="pi pi-spin pi-spinner" style={{ marginRight: '0.5rem' }} />
              Loading…
            </div>
          ) : templates.length === 0 ? (
            <div className="template-empty">
              <i className="pi pi-inbox" />
              <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>No templates</div>
              <div style={{ fontSize: '0.82rem' }}>This user has no uploaded templates.</div>
            </div>
          ) : (
            <div>
              {templates.map(name => (
                <div key={name} className="template-row">
                  <div className="template-name">
                    <FileTypeBadge name={name} />
                    <span>{name}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <Button
                      icon={previewLoading === name ? 'pi pi-spin pi-spinner' : 'pi pi-eye'}
                      text
                      rounded
                      size="small"
                      onClick={() => openPreview(name)}
                      tooltip="Preview"
                      tooltipOptions={{ position: 'left' }}
                      disabled={previewLoading === name}
                    />
                    <Button
                      icon="pi pi-download"
                      text
                      rounded
                      size="small"
                      onClick={() => adminDownloadTemplate(selectedEmail, name)}
                      tooltip="Download"
                      tooltipOptions={{ position: 'left' }}
                    />
                    <Button
                      icon="pi pi-trash"
                      severity="danger"
                      text
                      rounded
                      size="small"
                      onClick={() => handleDelete(name)}
                      tooltip="Delete"
                      tooltipOptions={{ position: 'left' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* ── Output rules ── */}
      {selectedEmail && templates.length > 0 && (
        <Card className="section-card">
          <div className="section-header">
            <div>
              <h3 className="m-0">Output Rules</h3>
              <p className="m-0 mt-1" style={{ fontSize: '0.82rem', color: '#64748b' }}>
                Folder and filename pattern for <strong>{selectedEmail}</strong>.
                Use <code style={{ background: '#f1f5f9', padding: '0 0.3rem', borderRadius: '0.2rem' }}>{'{COLUMN_NAME}'}</code> to insert data values.
              </p>
            </div>
            <Button
              label={rulesSaving ? 'Saving…' : 'Save rules'}
              icon={rulesSaving ? 'pi pi-spin pi-spinner' : 'pi pi-save'}
              disabled={!rulesDirty || rulesSaving}
              onClick={handleSaveRules}
            />
          </div>

          <div className="rules-table">
            <div className="rules-table-header">
              <span>Template</span>
              <span>Folder in zip</span>
              <span>Filename pattern</span>
            </div>
            {templates.map(name => {
              const rule = rules[name] ?? emptyRule();
              return (
                <div key={name} className="rules-table-row">
                  <div className="rules-table-name">
                    <FileTypeBadge name={name} />
                    <span style={{ fontSize: '0.85rem' }}>{name}</span>
                  </div>
                  <InputText
                    value={rule.folder}
                    onChange={e => updateRule(name, 'folder', e.target.value)}
                    placeholder="e.g. contracts/{YEAR}"
                    style={{ width: '100%', fontSize: '0.85rem' }}
                  />
                  <InputText
                    value={rule.filenameTemplate}
                    onChange={e => updateRule(name, 'filenameTemplate', e.target.value)}
                    placeholder={`e.g. {ID}_${name}`}
                    style={{ width: '100%', fontSize: '0.85rem' }}
                  />
                </div>
              );
            })}
          </div>

          <p className="m-0 mt-3" style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
            Leave a field empty to use the default: folder is the zip root, filename is <code>lookupKey_templateName</code>.
          </p>
        </Card>
      )}
    </>
  );
}
